import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BadgeCheck, TrendingUp, BookOpen, Users, Heart, Building2,
  Target, Eye, Compass, CheckCircle2, Briefcase, Inbox, Paperclip,
} from "lucide-react";
import { openMailto } from "@/lib/mailto";
import useSeo from "@/lib/useSeo";

const categories = [
  "Dental Billing", "AR & Collections", "Denial Management", "Insurance Verification",
  "Claims Processing", "Payment Posting", "Dental RCM Operations", "Client Support",
  "Quality & Compliance", "Sales & Business Development", "Technology & Automation", "Management & Leadership",
];

const whyCards = [
  { icon: BadgeCheck, t: "22+ Years of Experience", d: "Learn from an organization with decades of RCM industry knowledge." },
  { icon: TrendingUp, t: "Career Growth", d: "Opportunities to develop your skills and take on greater responsibilities." },
  { icon: BookOpen, t: "Continuous Learning", d: "Build expertise in dental billing, insurance, claims, AR, denials, and RCM operations." },
  { icon: Users, t: "Collaborative Culture", d: "Work with a professional team where collaboration and communication matter." },
  { icon: Heart, t: "Meaningful Work", d: "Help dental practices improve their revenue cycle and focus on patient care." },
  { icon: Building2, t: "Long-Term Opportunities", d: "Build a career in a growing and evolving RCM organization." },
];

const lifeStatements = [
  "Learn from experienced professionals.",
  "Share ideas.",
  "Develop new skills.",
  "Take ownership.",
  "Grow with the organization.",
];

const traits = [
  "Passionate about learning", "Detail-oriented", "Strong communication skills", "Customer-focused",
  "Team-oriented", "Responsible and accountable", "Interested in healthcare revenue cycle management", "Willing to grow professionally",
];

const openings = [];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const emptyForm = {
  full_name: "", email: "", phone: "", location: "", expertise: "",
  years_experience: "", position_interest: "", linkedin: "", message: "",
};

export default function Careers() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useSeo({
    title: "Careers at ClariDent RCM | Join Our Dental RCM Team",
    description: "Explore career opportunities at ClariDent RCM, a dental revenue cycle management company backed by 22+ years of industry experience. Grow your career in dental billing, AR, denials, and RCM operations.",
    path: "/careers",
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Full name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 7) errs.phone = "Enter a valid phone number.";
    if (!form.location.trim()) errs.location = "Current location is required.";
    if (!form.expertise) errs.expertise = "Select your area of expertise.";
    if (!form.years_experience) errs.years_experience = "Select your years of experience.";
    if (form.linkedin && !/^https?:\/\/.+\..+/.test(form.linkedin)) errs.linkedin = "Enter a valid LinkedIn URL (starting with https://).";
    return errs;
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields and try again.");
      return;
    }
    setErrors({});
    openMailto({
      subject: `Career Application — ${form.full_name}`,
      lines: [
        `Full Name: ${form.full_name}`,
        `Email: ${form.email}`,
        `Phone: ${form.phone}`,
        `Current Location: ${form.location}`,
        `Area of Expertise: ${form.expertise}`,
        `Years of Experience: ${form.years_experience}`,
        form.position_interest && `Position Interested In: ${form.position_interest}`,
        form.linkedin && `LinkedIn: ${form.linkedin}`,
        form.message && `\nCover Letter / Message:\n${form.message}`,
        `\nIMPORTANT: Please attach your resume/CV (PDF or DOC) to this email before sending.`,
      ],
    });
    toast.success("Opening your email app — please attach your resume before sending.");
    setForm(emptyForm);
  };

  const err = (k) => errors[k] ? <p className="text-xs text-red-500 mt-1 font-semibold" data-testid={`careers-error-${k}`}>{errors[k]}</p> : null;

  return (
    <main data-testid="careers-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(14,143,143,0.10),transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 items-center relative">
          <motion.div className="lg:col-span-7" initial="hidden" animate="show" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 px-4 py-2 text-xs font-bold text-teal-700 uppercase tracking-widest mb-7" data-testid="careers-trust-badge">
              <BadgeCheck size={15} /> 22+ Years of Industry Experience
            </div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900 leading-[1.05]" data-testid="careers-heading">
              Build Your Career. <span className="text-teal-600">Grow With Experience.</span>
            </h1>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl" data-testid="careers-subtext">
              Join Clarident RCM and become part of a team backed by 22+ years of experience in Dental Revenue Cycle Management.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => scrollTo("open-positions")} data-testid="careers-view-positions-btn" className="rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-700 hover:-translate-y-1 hover:shadow-xl">
                View Open Positions
              </button>
              <button onClick={() => scrollTo("apply")} data-testid="careers-submit-resume-btn" className="rounded-full border-2 border-navy-900 px-8 py-3.5 text-sm font-bold text-navy-900 transition-all duration-200 hover:bg-navy-50 hover:-translate-y-1">
                Submit Your Resume
              </button>
            </div>
          </motion.div>
          <motion.div className="lg:col-span-5" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } }}>
            <img src="/careers/team.jpg" alt="Clarident RCM team collaborating" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
          </motion.div>
        </div>
      </section>

      {/* AIM / VISION / MISSION */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          {[
            { icon: Target, t: "Our Aim", d: "To give every team member a place to do meaningful work, master their craft, and build a career they're proud of — while the dental practices we support grow stronger." },
            { icon: Eye, t: "Our Vision", d: "A workplace where experience is shared freely, ideas are welcomed at every level, and long-term careers in dental RCM are built on expertise and integrity." },
            { icon: Compass, t: "Our Mission", d: "To develop our people through real responsibility, continuous learning, and respectful teamwork — because a team that grows together delivers exceptional revenue cycle work for every practice we serve." },
          ].map((v, i) => (
            <motion.div
              key={v.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-lg transition-shadow duration-300"
              data-testid={`values-card-${i}`}
            >
              <v.icon size={28} className="text-teal-600 mb-5" />
              <h3 className="font-display text-2xl font-semibold text-navy-900">{v.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CLARIDENT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Why Clarident RCM?</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="why-careers-heading">
            More Than a Job. <span className="text-teal-600">A Career With Purpose.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            At Clarident RCM, you become part of an experienced organization where your skills, ideas, and professional development genuinely matter. We invest in people who want to master their craft — because when our team grows, our practices grow.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {whyCards.map((c, i) => (
            <motion.div
              key={c.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
              className="rounded-2xl border border-slate-200 bg-white p-8 group hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`why-card-${i}`}
            >
              <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center mb-5 group-hover:bg-teal-600 transition-colors duration-300">
                <c.icon size={22} className="text-teal-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-display text-xl font-semibold text-navy-900">{c.t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* LIFE AT CLARIDENT */}
      <section className="bg-navy-950 py-24 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <img src="/careers/handshake.jpg" alt="Professional collaboration at Clarident RCM" className="rounded-3xl w-full h-[420px] object-cover" />
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-400 mb-4">Life at Clarident</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold" data-testid="life-heading">
              Where Experience Meets Opportunity.
            </h2>
            <p className="mt-5 text-slate-400 text-sm sm:text-base leading-relaxed">
              We work as one team — experienced professionals mentoring, new ideas welcomed, and every person accountable for work that matters.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              {lifeStatements.map((s) => (
                <span key={s} className="rounded-full border border-slate-700 bg-navy-900/60 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-teal-500 hover:text-teal-400 transition-colors duration-200" data-testid={`life-chip-${lifeStatements.indexOf(s)}`}>
                  {s}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CAREER OPPORTUNITIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Career Opportunities</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="opportunities-heading">Find Your Opportunity</h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            We build teams across the full dental RCM spectrum. Explore the areas where your expertise can grow.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {categories.map((c, i) => (
            <motion.div
              key={c}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 4) * 0.06, duration: 0.4 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-3 hover:shadow-lg hover:-translate-y-1 hover:border-teal-500 transition-all duration-300"
              data-testid={`category-card-${i}`}
            >
              <Briefcase size={19} className="text-teal-600 shrink-0" />
              <h3 className="font-display text-sm sm:text-base font-semibold text-navy-900 leading-snug">{c}</h3>
            </motion.div>
          ))}
        </div>
        <p className="mt-10 text-center text-sm sm:text-base text-slate-600">
          Don't see the right position? <button onClick={() => scrollTo("apply")} className="font-bold text-teal-600 hover:underline" data-testid="categories-general-link">We still want to hear from you.</button>
        </p>
      </section>

      {/* OPEN POSITIONS */}
      <section id="open-positions" className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Open Positions</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="openings-heading">Current Openings</h2>
          </div>
          {openings.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-14 text-center max-w-2xl mx-auto" data-testid="no-openings-card">
              <div className="h-14 w-14 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-6">
                <Inbox size={26} className="text-teal-600" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-navy-900">No Current Openings</h3>
              <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-md mx-auto">
                Interested in joining Clarident RCM? Submit your resume and our team will contact you when a suitable opportunity becomes available.
              </p>
              <button onClick={() => scrollTo("apply")} data-testid="no-openings-apply-btn" className="mt-8 rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-700 hover:-translate-y-1 hover:shadow-xl">
                Submit Your Resume
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {openings.map((job) => (
                <div key={job.title} className="rounded-2xl border border-slate-200 bg-white p-8">{job.title}</div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WHO WE LOOK FOR */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Who We Look For</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="fit-heading">Are You a Good Fit?</h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            Titles change. Tools change. The people who thrive here share the same traits.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {traits.map((t, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 4) * 0.06, duration: 0.4 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 flex items-start gap-3 hover:shadow-md transition-shadow duration-300"
              data-testid={`trait-${i}`}
            >
              <CheckCircle2 size={19} className="text-teal-600 shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-navy-900 leading-snug">{t}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* EVP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-navy-950 px-8 py-16 sm:px-16 text-center relative overflow-hidden" data-testid="evp-section">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(14,143,143,0.25),transparent_65%)]" />
          <div className="relative max-w-2xl mx-auto">
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-white" data-testid="evp-heading">
              Your Experience Matters. <span className="text-teal-400">Your Growth Matters More.</span>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-slate-400 leading-relaxed">
              We value people who bring experience, curiosity, discipline, and a willingness to improve. Bring us your best — we'll give you real responsibility, honest feedback, and room to grow in an organization built on 22+ years of RCM knowledge.
            </p>
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="bg-slate-50 py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Career Application</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="apply-heading">Ready to Take the Next Step?</h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600">Tell us about yourself and explore opportunities to grow with Clarident RCM.</p>
          </div>
          <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 shadow-sm space-y-5" data-testid="careers-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="cf-name">Full Name *</Label>
                <Input id="cf-name" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className="mt-1.5 focus-visible:ring-teal-500" data-testid="careers-name-input" />
                {err("full_name")}
              </div>
              <div>
                <Label htmlFor="cf-email">Email Address *</Label>
                <Input id="cf-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1.5 focus-visible:ring-teal-500" data-testid="careers-email-input" />
                {err("email")}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="cf-phone">Phone Number *</Label>
                <Input id="cf-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="mt-1.5 focus-visible:ring-teal-500" data-testid="careers-phone-input" />
                {err("phone")}
              </div>
              <div>
                <Label htmlFor="cf-location">Current Location *</Label>
                <Input id="cf-location" value={form.location} onChange={set("location")} placeholder="City, State" className="mt-1.5 focus-visible:ring-teal-500" data-testid="careers-location-input" />
                {err("location")}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label>Area of Expertise *</Label>
                <Select value={form.expertise} onValueChange={(v) => setForm({ ...form, expertise: v })}>
                  <SelectTrigger className="mt-1.5 focus:ring-teal-500" data-testid="careers-expertise-select">
                    <SelectValue placeholder="Select your expertise" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {err("expertise")}
              </div>
              <div>
                <Label>Years of Experience *</Label>
                <Select value={form.years_experience} onValueChange={(v) => setForm({ ...form, years_experience: v })}>
                  <SelectTrigger className="mt-1.5 focus:ring-teal-500" data-testid="careers-experience-select">
                    <SelectValue placeholder="Select experience" />
                  </SelectTrigger>
                  <SelectContent>
                    {["0–1 years", "1–3 years", "3–5 years", "5–10 years", "10+ years"].map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                {err("years_experience")}
              </div>
            </div>
            <div>
              <Label htmlFor="cf-position">Position Interested In</Label>
              <Input id="cf-position" value={form.position_interest} onChange={set("position_interest")} placeholder="e.g. Denial Management, or General Application" className="mt-1.5 focus-visible:ring-teal-500" data-testid="careers-position-input" />
            </div>
            <div>
              <Label htmlFor="cf-linkedin">LinkedIn Profile</Label>
              <Input id="cf-linkedin" value={form.linkedin} onChange={set("linkedin")} placeholder="https://www.linkedin.com/in/yourprofile" className="mt-1.5 focus-visible:ring-teal-500" data-testid="careers-linkedin-input" />
              {err("linkedin")}
            </div>
            <div>
              <Label>Resume / CV</Label>
              <div className="mt-1.5 flex items-start gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-6 text-sm text-slate-600" data-testid="careers-resume-note">
                <Paperclip size={20} className="text-teal-600 shrink-0 mt-0.5" />
                <span>When you submit, your email app opens with your details pre-filled. <span className="font-semibold text-navy-900">Please attach your resume/CV (PDF or DOC)</span> to that email before sending it to Billing@claridentrcm.com.</span>
              </div>
            </div>
            <div>
              <Label htmlFor="cf-message">Cover Letter / Message</Label>
              <Textarea id="cf-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about your experience and what you'd bring to the team..." className="mt-1.5 focus-visible:ring-teal-500" data-testid="careers-message-input" />
            </div>
            <button
              type="submit"
              data-testid="careers-submit-btn"
              className="w-full rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-700 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
            >
              Submit Application
            </button>
          </form>
        </div>
      </section>

      {/* GENERAL CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="rounded-3xl bg-navy-950 px-8 py-16 sm:px-16 text-center relative overflow-hidden" data-testid="careers-final-cta">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(14,143,143,0.25),transparent_65%)]" />
          <div className="relative max-w-2xl mx-auto">
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-white">Don't See Your Role?</h2>
            <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed">
              Great opportunities don't always fit into a job title. If you believe your skills can add value to Clarident RCM, we'd love to hear from you.
            </p>
            <button onClick={() => scrollTo("apply")} data-testid="careers-final-cta-btn" className="mt-9 rounded-full bg-teal-600 px-10 py-4 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1 hover:shadow-2xl">
              Submit Your Resume
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
